import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Plane, Users, Info, DollarSign, Cloud, Sparkles } from 'lucide-react';

export const Travel = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      
      <section className="pt-56 md:pt-48 pb-20 px-6 sm:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-16">
            {/* Plane Icon - Inline SVG */}
            <div className="mb-8">
              <svg 
                width="48" 
                height="48" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#335B7A" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ display: 'block', margin: '0 auto' }}
              >
                <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
              </svg>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl font-bold blue-text mb-4">
              {t('travel.title')}
            </h1>
            <p className="font-sans text-base md:text-lg text-muted-foreground">
              {t('travel.subtitle')}
            </p>
          </div>

          {/* Accommodation Section */}
          <div className="mb-16 fade-in">
            <div className="border-2 border-primary cream-bg p-8">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-8 h-8 blue-text" />
                <h2 className="font-serif text-3xl font-bold blue-text">
                  {t('travel.accommodation.title')}
                </h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-sans text-lg font-bold blue-text mb-3 tracking-wide">
                    {t('travel.accommodation.recommendation')}
                  </h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    {t('travel.accommodation.description')}
                  </p>
                </div>

                {/* Pro Tip - Highlighted */}
                <div className="blue-bg p-6 border-2 border-primary">
                  <div className="flex items-start gap-3 mb-3">
                    <Users className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                    <h3 className="font-sans text-lg font-bold text-white tracking-wide">
                      {t('travel.accommodation.groupTip')}
                    </h3>
                  </div>
                  <p className="font-sans text-base text-white leading-relaxed mb-4">
                    {t('travel.accommodation.groupDescription')}
                  </p>
                  <div className="cream-bg p-4 border-2 border-white">
                    <p className="font-sans text-sm blue-text font-semibold">
                      💬 {t('travel.accommodation.whatsappPrompt')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Getting There Section */}
          <div className="mb-16 fade-in">
            <div className="border-2 border-primary cream-bg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Plane className="w-8 h-8 blue-text" />
                <h2 className="font-serif text-3xl font-bold blue-text">
                  {t('travel.gettingThere.title')}
                </h2>
              </div>

              <div className="space-y-6">
                {/* Airports */}
                <div>
                  <h3 className="font-sans text-lg font-bold blue-text mb-3 tracking-wide">
                    ✈️ {t('travel.gettingThere.airport')}
                  </h3>
                  <ul className="space-y-2 font-sans text-base text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.airportTivat')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.airportPodgorica')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.airportDubrovnik')}</span>
                    </li>
                  </ul>
                </div>

                {/* Transportation */}
                <div>
                  <h3 className="font-sans text-lg font-bold blue-text mb-3 tracking-wide">
                    🚗 {t('travel.gettingThere.transport')}
                  </h3>
                  <ul className="space-y-2 font-sans text-base text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.carRental')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.taxi')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="blue-text font-bold">•</span>
                      <span>{t('travel.gettingThere.rideshare')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Local Information Section */}
          <div className="mb-16 fade-in">
            <div className="border-2 border-primary cream-bg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Info className="w-8 h-8 blue-text" />
                <h2 className="font-serif text-3xl font-bold blue-text">
                  {t('travel.localInfo.title')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-sans text-base blue-text font-semibold mb-1">
                      💶 {t('travel.localInfo.currency')}
                    </p>
                  </div>
                  <div>
                    <p className="font-sans text-base blue-text font-semibold mb-1">
                      🗣️ {t('travel.localInfo.language')}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="font-sans text-base blue-text font-semibold mb-1">
                      ☀️ {t('travel.localInfo.weather')}
                    </p>
                  </div>
                  <div>
                    <p className="font-sans text-base blue-text font-semibold mb-1">
                      🎒 {t('travel.localInfo.whatToPack')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};
